/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/EventRegistrationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * @file EventRegistrationModal
 * Modal component for event attendee registration (company info, sector, personal data, optional interests).
 */
import { ReactComponent as CloseIcon } from "../../assets/icons/Events/CloseIcon.svg";
import useEventRegistrationModal from "../../hooks/useEventRegistrationModal.js";
import EventRegistrationForm from "./EventRegistrationForm.jsx";
import styles from "./index.module.css";

/**
 * Modal for registering a user as an attendee for an event.
 * Handles company info, sector/subsector/branch, personal data, and optional
 * commercial interests/expectations. Supports token refresh on expiry.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Callback when the modal is closed
 * @param {Object} props.event - Event object (e.g. _id, expectativasRequerido, interesesComercialesRequerido)
 * @param {Object} [props.user] - Current user; when tipoDeUsuario !== "4", form is pre-filled from API
 * @returns {JSX.Element}
 */
const EventRegistrationModal = ({ open, onClose, event, user }) => {
  const form = useEventRegistrationModal({ open, onClose, event, user });

  return (
    <>
      {form.open && <div className={styles.overlay}></div>}
      <dialog ref={form.dialogRef} className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.closeButtonContainer}>
            <button className={styles.closeButton} onClick={form.onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalTitle}>
            {form.t("components.eventRegistrationModal.header")}
          </div>
          <div className={styles.modalSubtitle}>
            {form.t("components.eventRegistrationModal.subheader")}
          </div>
        </div>

        <EventRegistrationForm
          formData={form.formData}
          setFormData={form.setFormData}
          isFormDisabled={form.isFormDisabled}
          sectors={form.sectors}
          subSectors={form.subSectors}
          branches={form.branches}
          selectSectorRef={form.selectSectorRef}
          selectSubsectorRef={form.selectSubsectorRef}
          selectBranchRef={form.selectBranchRef}
          setSubsectors={form.setSubsectors}
          setBranches={form.setBranches}
          showError={form.showError}
          event={form.event}
          getErrorMessage={form.getErrorMessage}
          getFieldValidationState={form.getFieldValidationState}
          handleFieldChange={form.handleFieldChange}
          handleChange={form.handleChange}
          handleAmbitoOperacionesChange={form.handleAmbitoOperacionesChange}
        />

        <div className={styles.modalFooter}>
          <div className={styles.modalButtons}>
            <button onClick={form.onClose} className={styles.cancelButton}>
              {form.t("components.eventRegistrationModal.cancelButton")}
            </button>
            <button
              className={styles.createButton}
              onClick={form.handleRegister}
              disabled={form.hasErrors || form.isFormIncomplete}
            >
              {form.t("components.eventRegistrationModal.registerButton")}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default EventRegistrationModal;
